// What the Archive window holds (CLAUDE.md: Archive).
//
// One archive for both games, a tab each, the daily's first, and a grid of
// numbered tiles most recent first in three states: solved, missed, unplayed.
// Running Order's grid runs to today's puzzle; Call Sheet's shows its whole
// pool, all playable.
//
// **Which puzzles exist is the server's** (`archive_list`): every daily that has
// run and every Call Sheet in the pool. **How each went is this device's**
// (lib/history.ts), which is why an archive round marks its tile while counting
// for nothing else.
//
// The tabs are gated separately, not the archive as a whole: Call Sheet shows
// from day one because a pool has its whole contents from the start, while
// **Running Order shows as soon as there is one past daily** — today's and
// yesterday's, from the second day — and grows by one a day (owner, 12 October;
// it used to wait for fourteen). **With the test code the gate is lifted** and
// every queued puzzle is listed, so the archive can be tried before anything
// has published.
//
// **Each tile says played or not played, never won or lost** (owner, 12
// October): a finished puzzle, won or lost, takes the one "played" highlight,
// and only an unplayed tile is grey.
//
// **It opens on the game you are on** (`initial`), and on Running Order
// anywhere else — falling back to Call Sheet while Running Order has no tab.
import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { ModalTabs } from './Modal.tsx';
import { PREVIEW, PUBLISHED_DAILIES, POOL_SIZE, previewResult } from '../dev/preview.ts';
import { archiveList, type ArchiveListing as Listing } from '../lib/archive.ts';
import { outcomes, type GameKey } from '../lib/history.ts';
import './ArchivePanel.css';

/** The listing holds today's daily too, so two means one past daily. */
const RUNNING_ORDER_TAB_AT = 2;


const descending = (count: number) => Array.from({ length: count }, (_, i) => count - i);

// Preview mode's invented archive, so the full grid can be looked at.
const PREVIEW_LISTING: Listing = {
  runningOrder: descending(PUBLISHED_DAILIES), callSheet: descending(POOL_SIZE), test: false
};

export function ArchivePanel({ onClose, initial = 'running-order' }: { onClose: () => void; initial?: GameKey }) {
  const [listing, setListing] = useState<Listing | null>(PREVIEW ? PREVIEW_LISTING : null);
  const [failed, setFailed] = useState(false);

  useEffect(() => {
    if (PREVIEW) return;
    let live = true;
    archiveList().then(
      (l) => { if (live) setListing(l); },
      () => { if (live) setFailed(true); });
    return () => { live = false; };
  }, []);

  const showDaily = !!listing && (listing.test || listing.runningOrder.length >= RUNNING_ORDER_TAB_AT);
  const tabs = [
    ...(showDaily ? [{ id: 'running-order' as GameKey, name: 'Running Order' }] : []),
    { id: 'call-sheet' as GameKey, name: 'Call Sheet' }
  ];
  const [chosen, setChosen] = useState<GameKey | null>(initial);
  const tab = chosen && tabs.some((t) => t.id === chosen) ? chosen : tabs[0]!.id;

  const numbers = !listing ? [] : tab === 'running-order' ? listing.runningOrder : listing.callSheet;
  const mine = outcomes(tab);
  // Won or lost is kept for the screen-reader label alone; the tile shows played.
  const stateOf = (n: number): 'won' | 'missed' | null => {
    if (PREVIEW) return previewResult(n);
    const o = mine.get(n);
    return !o ? null : o.solvedOn ? 'won' : 'missed';
  };

  return (
    <>
      {tabs.length > 1 && <ModalTabs tabs={tabs} selected={tab} onSelect={setChosen} />}

      {!listing ? (
        <p className="archive-empty">{failed ? 'The archive could not be loaded. Check your connection and try again.' : 'Loading…'}</p>
      ) : numbers.length === 0 ? (
        <p className="archive-empty">
          Nothing to play yet. Puzzles appear here as they are published.
        </p>
      ) : (
        // Keyed by tab, so a new tab opens at its top — the newest puzzles —
        // rather than wherever the other tab was scrolled to.
        <div className="archive-grid modal-scroll" key={tab}>
          {numbers.map((n) => {
            const result = stateOf(n);
            return (
              <Link
                className={`archive-tile${result ? ' is-played' : ''}`}
                // A played tile starts a replay: the first play's result stays
                // as it was, and the replay counts for nothing (owner, 13 October).
                to={`/${tab}/${n}${result ? '?replay' : ''}`}
                key={n}
                onClick={onClose}
                aria-label={`${tab === 'running-order' ? 'Running Order' : 'Call Sheet'} #${n}${
                  result === 'won' ? ', solved' : result === 'missed' ? ', missed' : ', not played'}`}
              >
                {n}
              </Link>
            );
          })}
        </div>
      )}
    </>
  );
}
